/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
  AbstractFormComponent,
  BuilderNavigationService,
  BuilderUserContext,
  CommonActions,
  FormActionPipelines,
  FormFlowState,
} from '@gh/builder-shared';
import { AelEvents } from '@gh/builder-shared-data';
import { ActionFlow, getRouteInitData, MutablePageState } from '@gh/common-shared';
import { DirtyType } from '@gh/common-shared-data';
import { DialogService, FormMode, yesAnswer, yesNoConfirmation } from '@gh/core-ui';
import { filter$ } from '@gh/rx/operators';

import { cloneDeep, pull } from 'lodash';

import { HeadingListBusinessService } from '../../resources/heading-list-business.service';
import { RfqHeadingListItem, updateHeadingItemsToSave } from '../../resources/rfq-structure.model';
import {
  HeadingLineDialogConfig,
  headingLineDialogRoute,
} from '../heading-line-dialog/heading-line-dialog.component';

@Component({
  standalone: false,
  selector: 'gh-heading-list',
  templateUrl: 'heading-list.component.html',
  styleUrls: ['heading-list.component.scss'],
})
export class HeadingListComponent extends AbstractFormComponent<RfqHeadingListItem[]> implements OnInit {
  form: MutablePageState<RfqHeadingListItem[]>;
  selectedItem?: RfqHeadingListItem;
  headingList: RfqHeadingListItem[];

  constructor(private route: ActivatedRoute,
              private pipelines: FormActionPipelines,
              private commonActions: CommonActions,
              private businessService: HeadingListBusinessService,
              private dialogService: DialogService,
              private navigationService: BuilderNavigationService,
              private userContext: BuilderUserContext) {
    super();

    this.form = new MutablePageState(FormMode.EditMode);
  }

  ngOnInit(): void {
    const { headingList } = getRouteInitData(this.route);

    this.headingList = cloneDeep(headingList);
    this.form.setValue(headingList);
  }

  get completeFlow(): ActionFlow<FormFlowState<RfqHeadingListItem[]>> {
    return this.pipelines.complete({
      execute: this.commonActions.execute(() =>
        this.businessService.update(updateHeadingItemsToSave(this.form.value))),
      finish: (state: FormFlowState<RfqHeadingListItem[]>) => {
        this.form.setValue(state.value);
        this.headingList = cloneDeep(state.value);
        this.form.resetDirty();
      },
    });
  }

  goToAel(): void {
    this.navigationService.goToAel({
      label: 'lbl_rfq_structure',
      entityType: 'MASTER_ZONE',
      eventTypeIds: [AelEvents.MasterZone.Edited],
      entityId: this.userContext.masterZone!.id,
    });
  }

  isHeadingDeleted(item: RfqHeadingListItem): boolean {
    return item.dirtyType === DirtyType.Removed;
  }

  onDropHeading(event: CdkDragDrop<RfqHeadingListItem>): void {
    moveItemInArray(this.form.value, event.previousIndex, event.currentIndex);
    this.form.setDirty();
  }

  onCreate(): void {
    this.dialogService.openWithInput(headingLineDialogRoute, <HeadingLineDialogConfig>{})
      .afterClosed().pipe(
      filter$(Boolean))
      .subscribe((headingListItem: any) => {
        this.form.value.push({ ...headingListItem, dirtyType: DirtyType.New });
        this.form.setDirty();
      });
  }

  onDelete(item: RfqHeadingListItem): void {
    this.dialogService.confirmation(yesNoConfirmation('msg_delete_heading', { code: item.description })).pipe(
      filter$(yesAnswer))
      .subscribe(() => {
        if (item.dirtyType !== DirtyType.New) {
          item.dirtyType = DirtyType.Removed;
          this.form.setDirty();
        } else {
          pull(this.form.value, item);
        }
      });
  }

  onEdit(item: RfqHeadingListItem): void {
    this.dialogService.openWithInput(headingLineDialogRoute, <HeadingLineDialogConfig>{
        headingLine: item,
      })
      .afterClosed().pipe(
      filter$(Boolean))
      .subscribe((headingListItem: any) => {
        item.description = headingListItem.description;
        item.dirtyType = item.dirtyType || DirtyType.Updated;
        this.form.setDirty();
      });
  }

  onItemClick(item: RfqHeadingListItem): void {
    this.selectedItem = item;
  }

  onMemoChange(memo: string): void {
    const { selectedItem } = this;

    if (selectedItem) {
      selectedItem.dirtyType = selectedItem.dirtyType || DirtyType.Updated;
      selectedItem.memo = memo;
    }
    this.form.setDirty();
  }

  onReset(): void {
    this.dialogService
      .confirmation(yesNoConfirmation('msg_reset')).pipe(
      filter$(yesAnswer))
      .subscribe(() => {
        this.selectedItem = void 0;
        this.form.setValue(cloneDeep(this.headingList));
        this.form.resetDirty();
      });
  }
}
